<script lang="ts">
	import * as prismic from '@prismicio/client';
	import { PrismicImage, PrismicRichText, SliceZone } from '@prismicio/svelte';
	import { components } from '$lib/slices';
	import { inview } from 'svelte-inview';
	import { ColorTrigger } from '$lib/helpers/color.trigger';
	import SectionTitleDisplay from '$lib/components/SectionTitleDisplay.svelte';
	import PrismicVideo from '$lib/components/PrismicVideo.svelte';
	import { hideFooter } from '$lib/variable.store';
	import { publicationDateToISO } from '$lib/helpers/app.helpers';
	hideFooter.set('false');

	export let data;

	let structuredImages: Array<string> = [];

	if (prismic.isFilled.image(data.page.data.banner_image)) {
		structuredImages.push(`"${data.page.data.banner_image.url}"`);
	}

	data.page.data.slices.forEach((slice) => {
		if (slice.slice_type == 'image') {
			let srcImage = slice.primary.image?.url as string;
			structuredImages.push(`"${srcImage}"`);
		}
	});
</script>

{@html `
<script type="application/ld+json">
    {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
		"url": "https://www.limegreentangerine.co.uk${data.page.url}",
		"description": "${data.page.data.meta_description}",
        "name": "${data.page.data.title}",
        "headline": "${prismic.asText(data.page.data.introduction)}",
        "image": [${structuredImages}],
        "datePublished": "${publicationDateToISO(data.page.first_publication_date)}",
        "dateModified": "${publicationDateToISO(data.page.last_publication_date)}",
        "author": [
            {
                "@type": "Organization",
                "name": "Limegreentangerine",
                "url": "https://www.limegreentangerine.co.uk"
            }
        ]
    }
</script>
`}

<section
	class="color-trigger__section"
	data-slice-type="color_trigger"
	data-slice-variation="default"
>
	<div
		class="color-trigger__point"
		style="--indicatorBackground:{data.page.data.background_color};--indicatorForeground:{data.page
			.data.foreground_color};"
		use:inview
		on:inview_enter={(event) => {
			const { node, scrollDirection } = event.detail;
			ColorTrigger(node, scrollDirection);
		}}
		on:inview_init={(event) => {
			const { node } = event.detail;
			ColorTrigger(node, { vertical: 'down', horizontal: 'right' });
		}}
		data-background={data.page.data.background_color}
		data-foreground={data.page.data.foreground_color}
	></div>
</section>

<section
	class="work__header"
	use:inview
	on:inview_enter={(event) => {
		const { node, scrollDirection } = event.detail;
		ColorTrigger(node, scrollDirection);
	}}
	on:inview_init={(event) => {
		const { node } = event.detail;
		ColorTrigger(node, { vertical: 'down', horizontal: 'right' });
	}}
	data-background={data.page.data.background_color}
	data-foreground={data.page.data.foreground_color}
>
	<div class="container">
		<h1 class="visually-hidden">{data.page.data.title}</h1>

		{#if prismic.isFilled.richText(data.page.data.introduction)}
			<div class="work__header--introduction">
				<div class="row">
					<div class="col-12 col-lg-8 col-hd-6">
						<PrismicRichText field={data.page.data.introduction} />
					</div>
				</div>
			</div>
		{/if}

		{#key data.page.id}
			<div
				class="work__header--media--container"
				class:has-video={prismic.isFilled.linkToMedia(data.page.data.banner_video)}
			>
				{#if prismic.isFilled.image(data.page.data.banner_image)}
					<PrismicImage
						field={data.page.data.banner_image}
						class="work__header--media image"
						loading="lazy"
					/>
				{/if}

				{#if prismic.isFilled.linkToMedia(data.page.data.banner_video)}
					<PrismicVideo
						field={data.page.data.banner_video}
						poster={data.page.data.banner_image}
						classes="work__header--media video"
						autoplay
						loop
						muted
					/>
				{/if}
			</div>
		{/key}
	</div>
</section>

<SliceZone slices={data.page.data.slices} {components} />

<SectionTitleDisplay />
